import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useApp } from "@/contexts/AppContext";
import { useActiveCountries } from "@/hooks/useAppData";
import { addStoreRequest } from "@/hooks/useFirestore";
import { getDeviceId } from "@/hooks/useLeads";
import { parseAndValidatePhone } from "@/security/phone";
import { phoneCountries } from "@/data/phone-countries";

interface StoreRequestSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface RequestForm {
  storeName: string;
  email: string;
  phone: string;
  contactPersonName: string;
  storeUrl: string;
  countryId: string;
}

const INITIAL_FORM: RequestForm = {
  storeName: "",
  email: "",
  phone: "",
  contactPersonName: "",
  storeUrl: "",
  countryId: "",
};

export function StoreRequestSheet({
  open,
  onOpenChange,
}: StoreRequestSheetProps) {
  const { selectedCountry } = useApp();
  const { countries } = useActiveCountries();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<RequestForm>(INITIAL_FORM);

  const set =
    (field: keyof RequestForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.storeName.trim()) {
      toast({
        title: "خطأ",
        description: "الرجاء إدخال اسم المتجر",
        variant: "destructive",
      });
      return;
    }
    if (
      !form.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      toast({
        title: "خطأ",
        description: "الرجاء إدخال بريد إلكتروني صحيح",
        variant: "destructive",
      });
      return;
    }
    if (!form.phone.trim()) {
      toast({
        title: "خطأ",
        description: "الرجاء إدخال رقم الهاتف",
        variant: "destructive",
      });
      return;
    }

    const countryId = form.countryId || selectedCountry;
    if (!countryId) {
      toast({
        title: "خطأ",
        description: "الرجاء اختيار الدولة",
        variant: "destructive",
      });
      return;
    }

    const selectedCountryData = countries.find((c) => c.id === countryId);
    if (!selectedCountryData) {
      toast({
        title: "خطأ",
        description: "الدولة المختارة غير صالحة",
        variant: "destructive",
      });
      return;
    }

    const phoneCountry = phoneCountries.find(
      (pc) =>
        pc.flag === selectedCountryData.flag ||
        pc.nameAr === selectedCountryData.nameAr,
    );
    if (!phoneCountry) {
      toast({
        title: "خطأ",
        description: "لا يمكن التحقق من رقم الهاتف لهذه الدولة",
        variant: "destructive",
      });
      return;
    }

    const phoneValidation = parseAndValidatePhone(
      form.phone.trim(),
      phoneCountry.code,
    );
    if (!phoneValidation.isValid) {
      toast({
        title: "خطأ",
        description: phoneValidation.error || "رقم الهاتف غير صحيح",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const deviceId = getDeviceId();
      const payload: Record<string, string> = {
        storeName: form.storeName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: phoneValidation.e164 || form.phone.trim(),
        countryId,
        deviceId,
      };
      if (form.contactPersonName.trim())
        payload.contactPersonName = form.contactPersonName.trim();
      if (form.storeUrl.trim()) payload.storeUrl = form.storeUrl.trim();

      await addStoreRequest(payload as Parameters<typeof addStoreRequest>[0]);
      toast({ title: "تم الإرسال", description: "سنراجع طلبك قريباً" });
      setForm(INITIAL_FORM);
      onOpenChange(false);
    } catch {
      toast({
        title: "خطأ",
        description: "حدث خطأ، يرجى المحاولة مجدداً",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl max-h-[90vh] flex flex-col p-0"
        dir="ltr"
      >
        <div className="px-6 pt-6 pb-4">
          <SheetHeader className="text-left">
            <SheetTitle>طلب إضافة متجر</SheetTitle>
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          <div className="space-y-5 pb-32 pt-2">
            <Field label="اسم المتجر *">
              <Input
                value={form.storeName}
                onChange={set("storeName")}
                placeholder="مثال: متجر نون"
                className="h-12 rounded-xl text-base"
                disabled={isSubmitting}
              />
            </Field>

            <Field label="البريد الإلكتروني *">
              <Input
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="example@email.com"
                className="h-12 rounded-xl text-base"
                dir="ltr"
                disabled={isSubmitting}
              />
            </Field>

            <Field label="رقم الهاتف *">
              <Input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="05XXXXXXXX"
                className="h-12 rounded-xl text-base"
                dir="ltr"
                disabled={isSubmitting}
              />
            </Field>

            <Field label="اسم المسؤول">
              <Input
                value={form.contactPersonName}
                onChange={set("contactPersonName")}
                placeholder="مثال: أحمد محمد"
                className="h-12 rounded-xl text-base"
                disabled={isSubmitting}
              />
            </Field>

            <Field label="الدولة *">
              <Select
                value={form.countryId || selectedCountry || ""}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, countryId: value }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger className="h-12 rounded-xl text-base">
                  <SelectValue placeholder="اختر الدولة" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.id} value={country.id}>
                      <div className="flex items-center gap-2">
                        <span>{country.flag}</span>
                        <span>{country.nameAr}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="رابط المتجر">
              <Input
                value={form.storeUrl}
                onChange={set("storeUrl")}
                placeholder="https://example.com"
                className="h-12 rounded-xl text-base"
                dir="ltr"
                disabled={isSubmitting}
              />
            </Field>

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full h-12 text-base font-semibold rounded-xl mt-6"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  جاري الإرسال...
                </>
              ) : (
                "إرسال الطلب"
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground block">
        {label}
      </label>
      {children}
    </div>
  );
}
